/** Heatmap renderer: GitHub-style contribution calendar. */
import { GraphOptions } from '../options';
import { formatDate } from '../shared/dates';
import { escapeXml, formatNumber } from '../shared/escape';
import { estimateTextWidth, paint, round, svgDocument } from '../shared/svg';
import { PreparedData } from '../shared/types';
import { buildGrid } from './grid';

const WEEKDAY_ROWS: Array<[number, string]> = [
    [1, 'Mon'],
    [3, 'Wed'],
    [5, 'Fri'],
];

interface Frame {
    width: number;
    height: number;
    /** Transform applied to the natural-size content. */
    transform: string;
}

/** Fits natural-size content into the requested width/height (0 = auto) keeping its ratio. */
const fitFrame = (naturalWidth: number, naturalHeight: number, options: GraphOptions): Frame => {
    const { width, height } = options;
    let scale = 1;
    let outWidth = naturalWidth;
    let outHeight = naturalHeight;

    if (width && height) {
        scale = Math.min(width / naturalWidth, height / naturalHeight);
        outWidth = width;
        outHeight = height;
    } else if (width) {
        scale = width / naturalWidth;
        outWidth = width;
        outHeight = Math.round(naturalHeight * scale);
    } else if (height) {
        scale = height / naturalHeight;
        outHeight = height;
        outWidth = Math.round(naturalWidth * scale);
    }

    if (scale === 1 && outWidth === naturalWidth && outHeight === naturalHeight)
        return { width: outWidth, height: outHeight, transform: '' };

    const dx = round((outWidth - naturalWidth * scale) / 2);
    const dy = round((outHeight - naturalHeight * scale) / 2);
    return {
        width: outWidth,
        height: outHeight,
        transform: `translate(${dx} ${dy}) scale(${round(scale * 10000) / 10000})`,
    };
};

export const renderHeatmap = (data: PreparedData, options: GraphOptions): string => {
    const { colors } = options;
    const grid = buildGrid(data.days);
    const weeks = Math.max(grid.weeks, 1);

    const fontSize = options.fontSize;
    const titleSize = Math.round(fontSize * 1.3);
    const cell = options.cellSize;
    const gap = options.cellGap;
    const step = cell + gap;
    const pad = options.padding;

    const headerHeight = options.showTitle || options.showTotal ? Math.round(titleSize * 2) : 0;
    const monthHeight = options.showMonths ? Math.round(fontSize * 1.6) : 0;
    const weekdayWidth = options.showWeekdays ? Math.round(fontSize * 2.6) : 0;
    const legendCell = Math.min(cell, Math.round(fontSize * 1.1));
    const legendHeight = options.showLegend
        ? Math.round(Math.max(legendCell, fontSize) + fontSize * 0.9)
        : 0;

    const gridWidth = weeks * step - gap;
    const gridHeight = 7 * step - gap;
    const gridX = pad + weekdayWidth;
    const gridY = pad + headerHeight + monthHeight;

    const titleText = options.title || data.username;
    const legendGap = Math.max(gap, 2);
    const legendStep = legendCell + legendGap;
    const legendWidth =
        estimateTextWidth('Less', fontSize) +
        6 +
        (5 * legendStep - legendGap) +
        6 +
        estimateTextWidth('More', fontSize);

    // Short ranges (e.g. 7 days) give a very narrow grid: widen the card so the header and
    // legend still fit instead of overlapping. For a full year the grid is always widest.
    const showTitleText = options.showTitle && Boolean(titleText);
    const headerWidth =
        (showTitleText ? estimateTextWidth(titleText, titleSize) : 0) +
        (options.showTotal ? estimateTextWidth(data.totalText, fontSize) : 0) +
        (showTitleText && options.showTotal ? fontSize * 2 : 0);
    const innerWidth = Math.ceil(
        Math.max(
            weekdayWidth + gridWidth,
            headerWidth,
            options.showLegend ? weekdayWidth + legendWidth : 0,
        ),
    );

    const naturalWidth = pad * 2 + innerWidth;
    const naturalHeight = pad * 2 + headerHeight + monthHeight + gridHeight + legendHeight;
    const frame = fitFrame(naturalWidth, naturalHeight, options);

    const parts: string[] = [];

    // Header: title on the left, total on the right (or on the left if there is no title).
    if (headerHeight) {
        const baseline = pad + titleSize;
        if (options.showTitle && titleText) {
            parts.push(
                `<text x="${pad}" y="${baseline}" font-size="${titleSize}" font-weight="600" fill="${paint(colors.text)}">${escapeXml(titleText)}</text>`,
            );
        }
        if (options.showTotal) {
            const alignRight = options.showTitle && Boolean(titleText);
            parts.push(
                `<text x="${alignRight ? naturalWidth - pad : pad}" y="${baseline}" font-size="${fontSize}"` +
                    `${alignRight ? ' text-anchor="end"' : ''} fill="${paint(colors.count)}">${escapeXml(data.totalText)}</text>`,
            );
        }
    }

    if (options.showMonths) {
        const y = gridY - Math.round(fontSize * 0.6);
        for (const month of grid.months) {
            parts.push(
                `<text x="${gridX + month.week * step}" y="${y}" font-size="${fontSize}" fill="${paint(colors.label)}">${month.label}</text>`,
            );
        }
    }

    if (options.showWeekdays) {
        for (const [row, label] of WEEKDAY_ROWS) {
            parts.push(
                `<text x="${pad}" y="${round(gridY + row * step + cell / 2)}" dy="0.35em" font-size="${fontSize}" fill="${paint(colors.label)}">${label}</text>`,
            );
        }
    }

    const levelColors = [colors.empty, colors.color1, colors.color2, colors.color3, colors.color4];
    const cells = grid.days.map((day) => {
        const attributes =
            `x="${gridX + day.week * step}" y="${gridY + day.weekday * step}" width="${cell}" height="${cell}"` +
            ` rx="${options.radius}" fill="${paint(levelColors[day.level])}"`;
        if (!options.showTooltips) return `<rect ${attributes}/>`;
        const label = `${day.count === 0 ? 'No' : formatNumber(day.count)} contribution${day.count === 1 ? '' : 's'} on ${formatDate(day.date)}`;
        return `<rect ${attributes}><title>${label}</title></rect>`;
    });
    parts.push(`<g>${cells.join('')}</g>`);

    if (options.showLegend) {
        const centerY =
            gridY + gridHeight + Math.round(fontSize * 0.9) + Math.max(legendCell, fontSize) / 2;
        const right = naturalWidth - pad;
        const moreWidth = estimateTextWidth('More', fontSize);
        const cellsStart = right - moreWidth - 6 - (5 * legendStep - legendGap);
        const legendCells = levelColors
            .map(
                (color, i) =>
                    `<rect x="${round(cellsStart + i * legendStep)}" y="${round(centerY - legendCell / 2)}" width="${legendCell}" height="${legendCell}" rx="${Math.min(options.radius, legendCell / 2)}" fill="${paint(color)}"/>`,
            )
            .join('');
        parts.push(
            `<g fill="${paint(colors.label)}" font-size="${fontSize}">` +
                `<text x="${round(cellsStart - 6)}" y="${round(centerY)}" dy="0.35em" text-anchor="end">Less</text>` +
                legendCells +
                `<text x="${right}" y="${round(centerY)}" dy="0.35em" text-anchor="end">More</text>` +
                '</g>',
        );
    }

    const content = parts.join('');
    return svgDocument({
        width: frame.width,
        height: frame.height,
        options,
        title: `${data.username}'s GitHub contribution graph`,
        description: data.totalText,
        body: frame.transform ? `<g transform="${frame.transform}">${content}</g>` : content,
    });
};
